import React, { useEffect, useRef, useState, useCallback } from 'react';
import './BarcodeScanner.css';

const trimTrailingSlash = (value) => String(value || '').replace(/\/+$/, '');
const API_BASE_URL = process.env.REACT_APP_API_URL
  ? trimTrailingSlash(process.env.REACT_APP_API_URL)
  : (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'http://localhost:5000/api'
    : '/api');

const isDigitsOnly = (value) => /^\d+$/.test(value);

const isValidEan13 = (barcode) => {
  if (!/^\d{13}$/.test(barcode)) return false;
  const digits = barcode.split('').map(Number);
  const check = digits[12];
  const sum = digits
    .slice(0, 12)
    .reduce((acc, digit, index) => acc + (index % 2 === 0 ? digit : digit * 3), 0);
  return ((10 - (sum % 10)) % 10) === check;
};

const isValidUpcA = (barcode) => {
  if (!/^\d{12}$/.test(barcode)) return false;
  const digits = barcode.split('').map(Number);
  const check = digits[11];
  const sum = digits
    .slice(0, 11)
    .reduce((acc, digit, index) => acc + (index % 2 === 0 ? digit * 3 : digit), 0);
  return ((10 - (sum % 10)) % 10) === check;
};

const isValidEan8 = (barcode) => {
  if (!/^\d{8}$/.test(barcode)) return false;
  const digits = barcode.split('').map(Number);
  const check = digits[7];
  const sum = digits
    .slice(0, 7)
    .reduce((acc, digit, index) => acc + (index % 2 === 0 ? digit * 3 : digit), 0);
  return ((10 - (sum % 10)) % 10) === check;
};

const isLikelyValidBarcode = (barcode) => {
  if (!isDigitsOnly(barcode)) return false;
  if (barcode.length === 13) return isValidEan13(barcode);
  if (barcode.length === 12) return isValidUpcA(barcode);
  if (barcode.length === 8) return isValidEan8(barcode);
  return false;
};

const fetchWithTimeout = async (url, options = {}, timeoutMs = 6000) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeout);
  }
};

const safeReadJson = async (response) => {
  const raw = await response.text();
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch (_error) {
    return null;
  }
};

// Inline version of BarcodeScanner for embedding in ItemScanner
const BarcodeScannerInline = ({ onScan, onClose }) => {
  const scannerRef = useRef(null);
  const nativeDetectorRef = useRef(null);
  const nativeLoopTimerRef = useRef(null);
  const [isScanning, setIsScanning] = useState(false);
  const [cameraStarted, setCameraStarted] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customProductData, setCustomProductData] = useState({
    name: '',
    brand: '',
    category: '',
    description: ''
  });
  const [isCreatingCustom, setIsCreatingCustom] = useState(false);
  const scanUsedRef = useRef(false);
  const processingRef = useRef(false);
  const lastScanTimeRef = useRef(0);
  const detectionCountsRef = useRef({});

  const stopScanner = useCallback(async () => {
    if (nativeLoopTimerRef.current) {
      clearInterval(nativeLoopTimerRef.current);
      nativeLoopTimerRef.current = null;
    }
    nativeDetectorRef.current = null;
    try {
      const Quagga = (await import('quagga')).default;
      if (Quagga && typeof Quagga.stop === 'function') {
        Quagga.stop();
        Quagga.offDetected();
      }
    } catch (stopError) {
      console.warn('Error stopping Quagga:', stopError);
    }
    setIsScanning(false);
  }, []);

  const handleClose = useCallback(async () => {
    await stopScanner();
    setIsInitialized(false);
    setScanResult(null);
    setError(null);
    setIsProcessing(false);
    setShowCustomForm(false);
    setIsCreatingCustom(false);
    setCameraStarted(false);
    scanUsedRef.current = false;
    processingRef.current = false;
    lastScanTimeRef.current = 0;
    detectionCountsRef.current = {};
    if (onClose) {
      onClose();
    }
  }, [onClose, stopScanner]);

  const requestCameraStart = useCallback(() => {
    setError(null);
    setScanResult(null);
    setShowCustomForm(false);
    setIsProcessing(false);
    setIsInitialized(false);
    scanUsedRef.current = false;
    processingRef.current = false;
    lastScanTimeRef.current = 0;
    detectionCountsRef.current = {};
    setCameraStarted(true);
  }, []);

  const fetchProductData = async (barcode) => {
    try {
      // First, check local custom products database
      const customResponse = await fetchWithTimeout(`${API_BASE_URL}/custom-products/barcode/${barcode}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        }
      }, 4000);

      if (customResponse.ok) {
        const customData = await safeReadJson(customResponse);
        if (!customData || !customData.product) {
          throw new Error('Product service returned an invalid response.');
        }
        const product = customData.product;
        return {
          name: product.name,
          brand: product.brand || '',
          categories: product.category || '',
          description: product.description || '',
          isCustom: true
        };
      }

      // Fallback to Open Food Facts with timeout
      const response = await fetchWithTimeout(
        `https://world.openfoodfacts.org/api/v0/product/${barcode}.json`,
        {},
        6000
      );
      const data = await safeReadJson(response);
      if (!data) {
        throw new Error('Product lookup service is temporarily unavailable.');
      }

      if (data.status === 1 && data.product) {
        const product = data.product;
        return {
          name: product.product_name || product.product_name_en || 'Unknown Product',
          brand: product.brands || '',
          categories: product.categories || '',
          description: product.generic_name || product.product_name || '',
          isCustom: false
        };
      }

      throw new Error('Product not found in database');
    } catch (lookupError) {
      if (lookupError.name === 'AbortError') {
        throw new Error('Lookup timed out. Please try scanning again.');
      }
      if (String(lookupError.message || '').toLowerCase().includes('not found')) {
        throw lookupError;
      }
      throw new Error('Could not verify this barcode right now. You can scan again or create a custom product.');
    }
  };

  const applyScannedProduct = useCallback((productData) => {
    const defaultExpiryDate = new Date();
    defaultExpiryDate.setDate(defaultExpiryDate.getDate() + 7);

    const product = {
      name: productData.name || 'Unknown Product',
      quantity: '1',
      location: 'pantry',
      expiryDate: defaultExpiryDate.toISOString().split('T')[0],
      description: productData.description || productData.brand || ''
    };

    if (onScan) {
      onScan(product);
    }
  }, [onScan]);

  const initializeScanner = useCallback(async () => {
    try {
      if (scanUsedRef.current) {
        return;
      }

      const Quagga = (await import('quagga')).default;

      if (!Quagga) {
        setError('Barcode scanner not available. Please refresh the page.');
        return;
      }

      try {
        // Mobile browsers can fail if this permission-check stream is left open.
        const permissionStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } }
        });
        permissionStream.getTracks().forEach((track) => track.stop());
      } catch (permError) {
        if (permError.name === 'NotAllowedError' || permError.name === 'PermissionDeniedError') {
          setError('Camera permission denied. Please allow camera access in your browser settings and refresh the page.');
          return;
        }
        throw permError;
      }

      const processDetectedCode = async (scannedCode) => {
        if (scanUsedRef.current || processingRef.current) return;
        scanUsedRef.current = true;
        processingRef.current = true;
        setIsProcessing(true);
        setScanResult({ barcode: scannedCode, status: 'loading' });

        try {
          const productData = await fetchProductData(scannedCode);
          await stopScanner();
          applyScannedProduct(productData);
        } catch (lookupError) {
          await stopScanner();
          setScanResult({
            barcode: scannedCode,
            status: 'error',
            error: lookupError.message || 'Product lookup failed'
          });
        } finally {
          processingRef.current = false;
          setIsProcessing(false);
        }
      };

      const startNativeDetectorLoop = () => {
        if (typeof window === 'undefined' || typeof window.BarcodeDetector !== 'function') return;

        try {
          nativeDetectorRef.current = new window.BarcodeDetector({
            formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'codabar']
          });
        } catch (_formatError) {
          try {
            nativeDetectorRef.current = new window.BarcodeDetector();
          } catch (_ctorError) {
            nativeDetectorRef.current = null;
            return;
          }
        }

        nativeLoopTimerRef.current = setInterval(async () => {
          if (scanUsedRef.current || processingRef.current || !nativeDetectorRef.current) return;
          const videoEl = scannerRef.current?.querySelector('video');
          if (!videoEl || videoEl.readyState < 2) return;

          try {
            const codes = await nativeDetectorRef.current.detect(videoEl);
            if (!Array.isArray(codes) || codes.length === 0) return;

            for (const code of codes) {
              const rawCode = String(code?.rawValue || '').trim();
              const scannedCode = rawCode.replace(/\D/g, '');
              if (!isLikelyValidBarcode(scannedCode)) continue;

              const now = Date.now();
              if (now - lastScanTimeRef.current < 250) return;
              lastScanTimeRef.current = now;
              await processDetectedCode(scannedCode);
              return;
            }
          } catch (_detectError) {
            // Keep loop running and rely on Quagga if native detection misses.
          }
        }, 300);
      };

      Quagga.init({
        inputStream: {
          type: 'LiveStream',
          target: scannerRef.current,
          constraints: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 },
            advanced: [{ focusMode: 'continuous' }]
          }
        },
        decoder: {
          readers: ['ean_reader', 'ean_8_reader', 'upc_reader', 'upc_e_reader', 'code_128_reader', 'code_39_reader', 'codabar_reader']
        },
        locate: true,
        locator: {
          patchSize: 'large',
          halfSample: true
        },
        numOfWorkers: 4,
        frequency: 25
      }, (err) => {
        if (err) {
          console.error('QuaggaJS initialization error:', err);
          setError('Failed to initialize camera. Please check camera permissions.');
          return;
        }
        Quagga.start();
        setIsScanning(true);
        setIsInitialized(true);
        startNativeDetectorLoop();
      });

      const detectionHandler = async (result) => {
        try {
          if (!result || !result.codeResult || !result.codeResult.code) return;

          const rawCode = String(result.codeResult.code).trim();
          const scannedCode = rawCode.replace(/\D/g, '');
          const confidence = result.codeResult.decodedCodes
            ? result.codeResult.decodedCodes.reduce((acc, code) => acc + code.error, 0) / result.codeResult.decodedCodes.length
            : 1;

          if (confidence > 0.6) return;
          if (!isLikelyValidBarcode(scannedCode)) return;

          const now = Date.now();
          if (now - lastScanTimeRef.current < 250) return;
          lastScanTimeRef.current = now;

          const nextCount = (detectionCountsRef.current[scannedCode] || 0) + 1;
          detectionCountsRef.current[scannedCode] = nextCount;
          if (scanUsedRef.current || processingRef.current) return;

          Quagga.offDetected(detectionHandler);
          await processDetectedCode(scannedCode);
        } catch (handlerError) {
          console.error('Error in barcode detection handler:', handlerError);
          processingRef.current = false;
          setIsProcessing(false);
        }
      };

      Quagga.onDetected(detectionHandler);
    } catch (initError) {
      console.error('Error initializing Quagga:', initError);
      setError('Failed to initialize barcode scanner.');
    }
  }, [applyScannedProduct, stopScanner]);

  const resetAndRescan = useCallback(async () => {
    setScanResult(null);
    setShowCustomForm(false);
    setCustomProductData({
      name: '',
      brand: '',
      category: '',
      description: ''
    });
    setError(null);
    setIsProcessing(false);
    setCameraStarted(true);
    scanUsedRef.current = false;
    processingRef.current = false;
    lastScanTimeRef.current = 0;
    detectionCountsRef.current = {};
    await stopScanner();
    setTimeout(() => {
      initializeScanner();
    }, 120);
  }, [initializeScanner, stopScanner]);

  const handleCreateCustomProduct = async () => {
    if (!scanResult?.barcode || !customProductData.name.trim()) return;

    try {
      setIsCreatingCustom(true);
      const response = await fetch(`${API_BASE_URL}/custom-products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          barcode: scanResult.barcode,
          name: customProductData.name.trim(),
          brand: customProductData.brand.trim(),
          category: customProductData.category.trim(),
          description: customProductData.description.trim()
        })
      });

      const data = await safeReadJson(response);
      if (!response.ok || !data || !data.product) {
        throw new Error((data && data.message) || 'Failed to create custom product');
      }

      applyScannedProduct({
        name: data.product.name,
        brand: data.product.brand,
        description: data.product.description
      });
    } catch (createError) {
      setError(createError.message || 'Failed to create custom product');
    } finally {
      setIsCreatingCustom(false);
    }
  };

  useEffect(() => {
    if (!cameraStarted || !scannerRef.current) return undefined;

    const timer = setTimeout(() => {
      initializeScanner();
    }, 120);

    return () => {
      clearTimeout(timer);
      stopScanner();
    };
  }, [cameraStarted, initializeScanner, stopScanner]);

  return (
    <div className="barcode-inline-panel">
      {error && (
        <div className="scanner-error" style={{ marginBottom: '20px' }}>
          <p>{error}</p>
          <button
            onClick={() => {
              setError(null);
              resetAndRescan();
            }}
            className="retry-btn"
          >
            Try Again
          </button>
        </div>
      )}

      {scanResult && scanResult.status === 'loading' && (
        <div className="loading" style={{ textAlign: 'center', padding: '20px' }}>
          <p>Looking up product information...</p>
        </div>
      )}

      {scanResult && scanResult.status === 'error' && !showCustomForm && (
        <div className="error" style={{ textAlign: 'center', padding: '20px' }}>
          <h4>❌ Product Not Found</h4>
          <p>Barcode: {scanResult.barcode}</p>
          <p>{scanResult.error || 'This product is not in our database.'}</p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '12px' }}>
            <button onClick={resetAndRescan} className="retry-btn">Scan Again</button>
            <button onClick={() => setShowCustomForm(true)} className="custom-btn">Create Custom Product</button>
            <button onClick={handleClose} className="retry-btn">Back</button>
          </div>
        </div>
      )}

      {showCustomForm && scanResult && (
        <div className="custom-product-form" style={{ marginTop: '20px' }}>
          <h3 style={{ color: '#2e7d32', marginBottom: '10px', fontSize: '18px', fontWeight: '600' }}>
            📝 Create Custom Product
          </h3>
          <p style={{ color: '#6b7280', marginBottom: '20px', fontSize: '14px' }}>
            Barcode: <strong>{scanResult.barcode}</strong>
          </p>
          <div className="form-group">
            <label>Product Name *</label>
            <input
              type="text"
              className="form-input"
              value={customProductData.name}
              onChange={(e) => setCustomProductData((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Enter product name"
              required
            />
          </div>
          <div className="form-group">
            <label>Brand</label>
            <input
              type="text"
              className="form-input"
              value={customProductData.brand}
              onChange={(e) => setCustomProductData((prev) => ({ ...prev, brand: e.target.value }))}
              placeholder="Enter brand name"
            />
          </div>
          <div className="form-group">
            <label>Category</label>
            <input
              type="text"
              className="form-input"
              value={customProductData.category}
              onChange={(e) => setCustomProductData((prev) => ({ ...prev, category: e.target.value }))}
              placeholder="e.g., Dairy, Snacks, Beverages"
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              className="form-textarea"
              value={customProductData.description}
              onChange={(e) => setCustomProductData((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Optional description"
              rows="3"
            />
          </div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px' }}>
            <button type="button" className="retry-btn" onClick={() => setShowCustomForm(false)} disabled={isCreatingCustom}>
              Cancel
            </button>
            <button
              type="button"
              className="custom-btn"
              onClick={handleCreateCustomProduct}
              disabled={!customProductData.name.trim() || isCreatingCustom}
            >
              {isCreatingCustom ? 'Saving...' : 'Save and Use'}
            </button>
          </div>
        </div>
      )}

      {!scanResult && !showCustomForm && !cameraStarted && !error && (
        <div className="upload-section barcode-upload-section">
          <div className="upload-main">
            <button
              className="camera-primary-btn"
              onClick={requestCameraStart}
              disabled={isProcessing}
            >
              <span className="camera-icon-large" aria-hidden="true">{'\uD83D\uDCF7'}</span>
              <span>Scan Barcode</span>
            </button>
          </div>
        </div>
      )}

      {!scanResult && !showCustomForm && cameraStarted && (
        <div className="scanner-container no-frame barcode-live-scanner">
          <div ref={scannerRef} className="scanner-video" />
          {!isScanning && !error && !isInitialized && (
            <div className="scanner-placeholder">
              <p>Initializing camera...</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BarcodeScannerInline;
